'use client';

import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function PrivacyPolicyPage() {
  const lastUpdated = 'June 1, 2026';

  const sections = [
    {
      title: '1. Information We Collect',
      content: [
        { subtitle: 'Personal Information', text: 'When you apply for financing or create an account, we collect: full name, email address, phone number, mailing address, Social Security Number (for identity verification), date of birth, and financial information including income, credit score, and bank account details.' },
        { subtitle: 'Business Information', text: 'For business loan applications, we collect: business name, EIN/Tax ID, business address, industry type, annual revenue, time in business, number of employees, and business financial statements.' },
        { subtitle: 'Documents', text: 'We collect identity documents (driver\'s license, Social Security card), financial documents (pay stubs, tax returns, bank statements), and verification photos (selfie with ID).' },
        { subtitle: 'Usage Data', text: 'We automatically collect information about how you interact with our platform, including IP address, browser type, pages visited, and time spent on pages.' },
      ],
    },
    {
      title: '2. How We Use Your Information',
      content: [
        { subtitle: 'Loan Matching', text: 'We use your information to match you with appropriate lending partners from our network. Your information is shared with lenders only after you submit an application and agree to our terms.' },
        { subtitle: 'Identity Verification', text: 'We use your personal and document information to verify your identity and prevent fraud.' },
        { subtitle: 'Communications', text: 'We use your contact information to send you application updates, loan offers, and important account notifications.' },
        { subtitle: 'Service Improvement', text: 'We analyze usage patterns to improve our platform and user experience.' },
      ],
    },
    {
      title: '3. Information Sharing',
      content: [
        { subtitle: 'Lending Partners', text: 'With your consent, we share your application information with our network of lending partners to facilitate loan matching. We do not sell your personal information to third parties.' },
        { subtitle: 'Service Providers', text: 'We work with trusted service providers for identity verification, document storage, and payment processing. These providers are contractually bound to protect your information.' },
        { subtitle: 'Legal Requirements', text: 'We may disclose your information when required by law, court order, or government regulation.' },
      ],
    },
    {
      title: '4. Data Security',
      content: [
        { subtitle: 'Encryption', text: 'All data transmitted to and from our platform is encrypted using TLS/SSL. Documents and sensitive files are encrypted at rest using AES-256 encryption.' },
        { subtitle: 'Access Controls', text: 'Access to your personal information is restricted to authorized personnel who need it to provide our services. We use role-based access controls and audit logging.' },
        { subtitle: 'Storage', text: 'Your data is stored on secure servers hosted by Supabase, which maintains SOC 2 Type II compliance.' },
      ],
    },
    {
      title: '5. Your Rights',
      content: [
        { subtitle: 'Access & Portability', text: 'You have the right to access the personal information we hold about you and request a copy in a portable format.' },
        { subtitle: 'Correction', text: 'You can update your personal information at any time through your account settings.' },
        { subtitle: 'Deletion', text: 'You may request deletion of your account and associated data. Note that we may retain certain information as required by law or for legitimate business purposes.' },
        { subtitle: 'Opt-Out', text: 'You can opt out of marketing communications at any time by clicking "unsubscribe" in any email or updating your notification preferences.' },
      ],
    },
    {
      title: '6. Cookies & Tracking',
      content: [
        { subtitle: 'Essential Cookies', text: 'We use essential cookies to maintain your session and provide core functionality. These cannot be disabled.' },
        { subtitle: 'Analytics', text: 'We use analytics cookies to understand how users interact with our platform. You can opt out of analytics tracking in your browser settings.' },
      ],
    },
    {
      title: '7. Children\'s Privacy',
      content: [
        { subtitle: '', text: 'Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected information from a minor, please contact us immediately.' },
      ],
    },
    {
      title: '8. Changes to This Policy',
      content: [
        { subtitle: '', text: 'We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by posting a notice on our platform. Your continued use of our services after changes take effect constitutes acceptance of the updated policy.' },
      ],
    },
    {
      title: '9. Contact Us',
      content: [
        { subtitle: '', text: 'If you have questions about this Privacy Policy or how we handle your data, please contact our Privacy Team at privacy@lendo.com or write to us at: Lendo LLC, Privacy Team, 123 Financial District, New York, NY 10004.' },
      ],
    },
  ];

  return (
    <>
      <Navbar />
      <main style={{ paddingTop: '88px', paddingBottom: '80px', minHeight: '100vh', background: '#f7f9fc' }}>
        {/* Header */}
        <div style={{ background: '#1a2b4a', padding: '60px 24px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ fontFamily: 'Merriweather, serif', fontWeight: 900, fontSize: '2.2rem', color: '#ffffff', margin: '0 0 12px' }}>
              Privacy Policy
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', margin: 0 }}>
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 0' }}>
          {/* Intro */}
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', marginBottom: '24px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', color: '#4a5568', lineHeight: 1.8, margin: 0 }}>
              Lendo LLC (&quot;Lendo,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our loan matching platform and related services. Please read this policy carefully.
            </p>
          </div>

          {/* Sections */}
          {sections?.map((section, i) => (
            <div key={i} style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', marginBottom: '16px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 700, fontSize: '1.15rem', color: '#1a2b4a', margin: '0 0 20px' }}>
                {section?.title}
              </h2>
              {section?.content?.map((item, j) => (
                <div key={j} style={{ marginBottom: j < section?.content?.length - 1 ? '16px' : 0 }}>
                  {item?.subtitle && (
                    <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.92rem', color: '#2d3748', margin: '0 0 6px' }}>
                      {item?.subtitle}
                    </h3>
                  )}
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: '#4a5568', lineHeight: 1.75, margin: 0 }}>
                    {item?.text}
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}
